import { getSwatches, type SwatchMap } from "colorthief";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { Pressable } from "../../../Pressable";
import { Heading } from "../../../typography/Heading";
import { Label } from "../../../typography/Label";
import { useMessage } from "../Context";
import { proposalImageClass } from "../styles";
import type { ChatArticle, ChatTaggedBooking } from "../types";

export function Articles() {
    const { articles } = useMessage();

    if (articles.length === 1) {
        const article = articles[0];

        return <ArticleHero key={article.id} article={article} colorStop={80} />;
    }

    return (
        <div className="ml-32 flex w-[calc(100%-var(--spacing-32))] flex-col items-start gap-8">
            {articles.map((article) => (
                <Pressable key={article.id} href={article.href} className="text-left">
                    <div className="flex w-full flex-row items-center gap-16">
                        {article.imageUrl && (
                            <img
                                src={article.imageUrl}
                                alt={article.title}
                                className="size-80 min-h-80 min-w-80 object-cover md:size-96 md:min-h-96 md:min-w-96"
                            />
                        )}

                        <div className="flex w-full flex-col items-start gap-8">
                            <Heading level={4}>{article.title}</Heading>

                            {article.subtitle && (
                                <Label size="medium" weight="regular" color="secondary">
                                    {article.subtitle}
                                </Label>
                            )}
                        </div>
                    </div>
                </Pressable>
            ))}
        </div>
    );
}

function ArticleHero({
    article,
    colorStop,
}: {
    article: ChatArticle;
    colorStop: number;
}) {
    const [gradientColor, setGradientColor] = useState("rgb(0, 0, 0)");
    const gradientColorStop = Math.min(Math.max(colorStop, 0), 100);

    async function handleImageLoad(image: HTMLImageElement) {
        let swatches: SwatchMap | undefined;

        try {
            swatches = await getSwatches(image, { quality: 10 });
        } catch {
            // Keep the black fallback when image pixels cannot be sampled.
        }

        const rgb = getDarkestColor(swatches);

        if (rgb) {
            setGradientColor(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`);
        }
    }

    return (
        <Pressable href={article.href}>
            <div className="relative ml-32 h-[156px] w-[282px] overflow-hidden rounded-rounded md:h-[180px] md:w-[320px]">
                {article.imageUrl && (
                    <img
                        src={article.imageUrl}
                        alt=""
                        crossOrigin="anonymous"
                        className="absolute inset-0 h-full w-full object-cover object-center"
                        onLoad={(event) => {
                            void handleImageLoad(event.currentTarget);
                        }}
                    />
                )}

                <div
                    className="relative flex h-full w-full items-end p-8"
                    style={{
                        background: `linear-gradient(to top, ${gradientColor} 0%, transparent ${gradientColorStop}%)`,
                    }}
                >
                    <div className="flex w-full flex-row items-center justify-between gap-8">
                        <Heading level={4} className="!text-white">
                            {article.title}
                        </Heading>

                        <div className="flex size-40 min-h-40 min-w-40 items-center justify-center rounded-full">
                            <ArrowRight
                                aria-hidden="true"
                                className="size-24 text-text-inverse-primary"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Pressable>
    );
}

function getDarkestColor(swatches: SwatchMap | undefined) {
    let darkestColor: { r: number; g: number; b: number } | undefined;
    let darkestLuminance = Number.POSITIVE_INFINITY;

    for (const swatch of Object.values(swatches ?? {})) {
        const rgb = swatch?.color.rgb();

        if (!rgb) {
            continue;
        }

        const luminance = rgb.r * 0.2126 + rgb.g * 0.7152 + rgb.b * 0.0722;

        if (luminance < darkestLuminance) {
            darkestColor = rgb;
            darkestLuminance = luminance;
        }
    }

    return darkestColor;
}

export function TaggedArticle({ article }: { article: ChatArticle }) {
    return (
        <Pressable href={article.href} className="text-left">
            <div className="flex w-full flex-row items-center gap-16">
                {article.imageUrl && (
                    <img
                        src={article.imageUrl}
                        alt={article.title}
                        className={proposalImageClass}
                    />
                )}

                <div className="flex w-full flex-col items-start gap-4">
                    <Heading level={5} className="line-clamp-2 text-left">
                        {article.title}
                    </Heading>
                </div>
            </div>
        </Pressable>
    );
}

export function TaggedBooking({ booking }: { booking: ChatTaggedBooking }) {
    return (
        <div className="flex w-full flex-row items-center gap-16">
            {booking.imageUrl && (
                <img
                    src={booking.imageUrl}
                    alt={booking.title}
                    className={proposalImageClass}
                />
            )}

            <div className="flex w-full flex-col items-start gap-4">
                <Heading level={5} className="line-clamp-2 text-left">
                    {booking.title}
                </Heading>

                {booking.optionTitle && (
                    <Label size="small" weight="regular">
                        {booking.optionTitle}
                    </Label>
                )}
            </div>
        </div>
    );
}
